import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Sun, Zap, Droplets, Wrench, Phone, MessageCircle, ChevronLeft, ChevronRight,
  Award, Cog, Package, Handshake, Building2, Headphones, Home as HomeIcon,
  Factory, CheckCircle2, ArrowRight, ShieldCheck,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { MaskLine, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { SolarCalculator } from "@/components/SolarCalculator";
import { useQuote } from "@/components/QuoteModal";
import { useContent } from "@/lib/content";

const HIGHLIGHT_ICONS = [Sun, Zap, Droplets, Wrench];
const WHY_ICONS = [Award, Cog, Package, Handshake, Building2, Headphones];
const SOLUTION_ICONS = { "residential-solar": HomeIcon, "commercial-solar": Building2, "industrial-solar": Factory, "solar-pump-sets": Droplets };

const Hero = () => {
  const { SITE, HOME } = useContent();
  const SLIDES = HOME.slides;
  const [idx, setIdx] = useState(0);
  const { openQuote } = useQuote();

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, [SLIDES.length]);

  const slide = SLIDES[idx % SLIDES.length];

  return (
    <section className="relative h-[86vh] min-h-[560px] overflow-hidden bg-brand-navy" data-testid="hero-section">
      <AnimatePresence mode="sync">
        <motion.div
          key={idx}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <img src={slide.image} alt="Solar panel installation" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/70 to-brand-navy/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-transparent to-brand-navy/30" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-4 sm:px-6">
        <AnimatePresence mode="wait">
          <motion.div key={idx} initial="hidden" animate="show" exit={{ opacity: 0, transition: { duration: 0.25 } }}>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-brand-orange"
              data-testid="hero-eyebrow"
            >
              <span className="h-px w-10 bg-brand-orange" /> {slide.eyebrow}
            </motion.p>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[1.04] tracking-tight text-white" data-testid="hero-heading">
              {slide.lines.map((line, i) => (
                <MaskLine key={line} delay={0.25 + i * 0.14}>
                  {i === slide.lines.length - 1 ? <span className="text-brand-orange">{line}</span> : line}
                </MaskLine>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.65 }}
              className="mt-5 max-w-xl text-base sm:text-lg text-white/75"
              data-testid="hero-subheading"
            >
              {slide.sub}
            </motion.p>
          </motion.div>
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.85 }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <button
            onClick={() => openQuote()}
            className="rounded-full bg-brand-orange px-8 py-4 font-heading text-sm font-bold uppercase tracking-wider text-white transition-all hover:bg-brand-orangedark hover:shadow-xl hover:shadow-brand-orange/30"
            data-testid="hero-get-quote-btn"
          >
            Get Free Quote
          </button>
          <a
            href={SITE.phone1}
            className="flex items-center gap-2 rounded-full border border-white/30 px-7 py-4 font-heading text-sm font-bold uppercase tracking-wider text-white backdrop-blur-sm transition-colors hover:border-brand-orange hover:text-brand-orange"
            data-testid="hero-call-btn"
          >
            <Phone className="h-4 w-4" /> Call Us Now
          </a>
          <a
            href={SITE.whatsapp}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-4 font-heading text-sm font-bold uppercase tracking-wider text-white transition-all hover:brightness-110"
            data-testid="hero-whatsapp-btn"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp Us
          </a>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 items-center gap-4 sm:left-auto sm:right-8 sm:translate-x-0">
        <button onClick={() => setIdx((idx - 1 + SLIDES.length) % SLIDES.length)} className="rounded-full border border-white/25 p-2.5 text-white transition-colors hover:border-brand-orange hover:text-brand-orange" aria-label="Previous slide" data-testid="hero-prev-btn">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all duration-500 ${i === idx ? "w-8 bg-brand-orange" : "w-3 bg-white/40"}`}
              aria-label={`Slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
        <button onClick={() => setIdx((idx + 1) % SLIDES.length)} className="rounded-full border border-white/25 p-2.5 text-white transition-colors hover:border-brand-orange hover:text-brand-orange" aria-label="Next slide" data-testid="hero-next-btn">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
};

export default function Home() {
  const { SITE, IMAGES, HOME, FAQS, PROCESS_STEPS, BRANDS, PRODUCTS, SOLUTIONS } = useContent();
  const { openQuote } = useQuote();
  return (
    <>
      <Seo
        title="Solar Company in Chennai | Rooftop Solar & Solar Pump | DHIYAA SOLAR ENERGY"
        description="DHIYAA SOLAR ENERGY provides rooftop solar, on-grid, hybrid, off-grid, commercial, industrial and solar pump solutions in Chennai and Tamil Nadu. Get a free solar consultation."
      />
      <Hero />

      {/* Highlights */}
      <section className="relative z-10 mx-auto -mt-16 max-w-7xl px-4 sm:px-6" data-testid="highlights-section">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {HOME.highlights.map((h, i) => {
            const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length];
            return (
              <Reveal key={h.title + i} delay={i * 0.08}>
                <div className="group h-full rounded-2xl border border-slate-100 bg-white p-6 shadow-lg shadow-slate-200/60 transition-all hover:-translate-y-1.5 hover:shadow-xl">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange transition-colors group-hover:bg-brand-orange group-hover:text-white">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-brand-navy">{h.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600">{h.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <div className="mt-16">
        <Marquee items={["Solar Rooftop Solutions", "Professional Installation", "Quality Components", "PM Surya Ghar Subsidy Support", "Solar Pump Sets", "After-Sales Support"]} />
      </div>

      {/* Solutions */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="solutions-section">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Our Solar Solutions</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-navy">
            Solar Energy for Every Roof, Field & Factory
          </h2>
          <p className="mt-3 text-slate-600">
            Customised solar solutions based on electricity consumption, available roof or land area, application, budget and future energy requirements.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SOLUTIONS.slice(0, 4).map((s, i) => {
            const Icon = SOLUTION_ICONS[s.slug] || Sun;
            return (
              <Reveal key={s.slug} delay={i * 0.08}>
                <Link
                  to={`/solutions/${s.slug}`}
                  className="group relative block h-96 overflow-hidden rounded-2xl shadow-lg"
                  data-testid={`solution-card-${s.slug}`}
                >
                  <img src={s.image} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-orange text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-4 font-heading text-xl font-bold text-white">{s.name}</h3>
                    <p className="mt-1 text-sm text-white/70">{s.tagline}</p>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange opacity-0 transition-all duration-300 group-hover:opacity-100">
                      Explore <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
        <Reveal className="mt-8 flex flex-wrap gap-3">
          {SOLUTIONS.slice(4).map((s) => (
            <Link
              key={s.slug}
              to={`/solutions/${s.slug}`}
              className="rounded-full border border-brand-blue/20 px-5 py-2.5 text-sm font-semibold text-brand-blue transition-colors hover:bg-brand-blue hover:text-white"
              data-testid={`solution-pill-${s.slug}`}
            >
              {s.name}
            </Link>
          ))}
        </Reveal>
      </section>

      {/* Why choose us */}
      <section className="bg-brand-sky py-20" data-testid="why-us-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Why Choose Us?</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-navy">
              Why DHIYAA SOLAR ENERGY?
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {HOME.whyUs.map((w, i) => {
              const Icon = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <Reveal key={w.title + i} delay={i * 0.06}>
                  <div className="group h-full rounded-2xl border border-slate-100 bg-white p-7 transition-all hover:-translate-y-1 hover:border-brand-blue/20 hover:shadow-xl">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue transition-colors group-hover:bg-brand-blue group-hover:text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-4 font-heading text-lg font-bold text-brand-navy">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PM Surya Ghar */}
      <section className="relative overflow-hidden bg-brand-navy py-20 sun-rays grain" data-testid="pm-surya-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">PM Surya Ghar: Muft Bijli Yojana</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              Make Your Home a <span className="text-brand-orange">Solar Home</span>
            </h2>
            <p className="mt-4 text-white/70">
              The PM Surya Ghar: Muft Bijli Yojana is the Government of India's rooftop solar programme for residential
              consumers, providing Central Financial Assistance for eligible residential rooftop solar installations.
            </p>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {HOME.pmSubsidy.map((s) => (
                <div key={s.l} className="rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                  <p className="font-heading text-xl sm:text-2xl font-extrabold text-brand-green brightness-150">{s.v}</p>
                  <p className="mt-1 text-[11px] leading-snug text-white/55">{s.l}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {HOME.pmSteps.map((s, i) => (
                <span key={s} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-white/80">
                  <span className="font-heading font-extrabold text-brand-orange">{i + 1}</span> {s}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/pm-surya-ghar"
                className="rounded-full bg-brand-green px-7 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-white transition-all hover:brightness-110"
                data-testid="pm-surya-check-btn"
              >
                Check Eligibility
              </Link>
              <Link
                to="/calculator"
                className="rounded-full border border-white/25 px-7 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
                data-testid="pm-surya-calc-btn"
              >
                Calculate Savings
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-brand-orange/15 blur-2xl" />
              <img src={IMAGES.pmSurya} alt="Residential rooftop solar under PM Surya Ghar" className="relative w-full rounded-3xl border border-white/10 object-cover shadow-2xl" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Products */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="products-section">
        <div className="grid items-center gap-12 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Our Products</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-navy">
              Solar Products & Components
            </h2>
            <p className="mt-3 text-slate-600">
              Complete solar system solutions using carefully selected components suitable for residential, commercial,
              industrial and agricultural applications.
            </p>
            <img src={IMAGES.products} alt="Solar panels and components" className="mt-6 w-full rounded-2xl object-cover shadow-lg" />
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-3">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.name + i} delay={i * 0.05}>
                <div className="h-full rounded-xl border border-slate-100 bg-white p-5 transition-all hover:border-brand-orange/30 hover:shadow-lg">
                  <CheckCircle2 className="h-5 w-5 text-brand-green" />
                  <h3 className="mt-2.5 font-heading text-base font-bold text-brand-navy">{p.name}</h3>
                  <p className="mt-1 text-[13px] leading-relaxed text-slate-600">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="border-y border-slate-100 bg-white py-14" data-testid="brands-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Technology & Product Brands</p>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-extrabold text-brand-navy">We Work With Reputed Solar Brands</h2>
          </Reveal>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            {BRANDS.map((b, i) => {
              const name = typeof b === "string" ? b : b.name;
              const logo = typeof b === "object" && b ? b.logo : null;
              return (
                <Reveal key={name + i} delay={i * 0.06}>
                  <span className="flex h-20 items-center justify-center rounded-2xl border border-slate-200 bg-white px-8 shadow-sm transition-all hover:border-brand-orange hover:shadow-lg" data-testid={`brand-${name.toLowerCase().replace(/\s+/g, "-")}`}>
                    {logo ? (
                      <img src={logo} alt={`${name} logo`} className="max-h-12 max-w-[140px] object-contain" />
                    ) : (
                      <span className="font-heading text-lg font-extrabold tracking-wide text-brand-blue">{name}</span>
                    )}
                  </span>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">
            Brand availability and product selection are subject to project requirements, technical specifications and current market availability.
          </p>
        </div>
      </section>

      {/* Process */}
      <section className="bg-brand-sky py-20" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">From Sunlight to Savings</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-navy">How It Works</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {PROCESS_STEPS.slice(0, 5).map((s, i) => (
              <Reveal key={s.n + i} delay={i * 0.07}>
                <div className="relative h-full rounded-2xl bg-white p-6 shadow-sm transition-all hover:shadow-lg">
                  <span className="font-heading text-4xl font-extrabold text-brand-orange/20">{s.n}</span>
                  <h3 className="mt-2 font-heading text-base font-bold text-brand-navy">{s.title}</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8 text-center">
            <Link to="/process" className="inline-flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wider text-brand-blue hover:text-brand-orange transition-colors" data-testid="process-view-all">
              View the full 9-step process <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Projects */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="projects-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Our Solar Projects</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-navy">
              Recent Installations Across Chennai
            </h2>
          </div>
          <Link to="/projects" className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wider text-brand-blue hover:text-brand-orange transition-colors" data-testid="projects-view-all">
            View all projects <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          {IMAGES.projects.slice(0, 4).map((p, i) => (
            <Reveal key={p.src + i} delay={i * 0.1}>
              <Link to="/projects" className="group relative block overflow-hidden rounded-2xl shadow-lg" data-testid={`project-card-${i}`}>
                <img src={p.src} alt={`${p.type} at ${p.location}`} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-transparent to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="font-heading text-lg font-bold text-white">{p.type}</p>
                  <p className="text-sm text-white/70">{p.location}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Calculator */}
      <section className="bg-brand-sky py-20" data-testid="calculator-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Solar Calculator</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-navy">
              How Much Can Your Rooftop Save?
            </h2>
            <p className="mt-3 text-slate-600">
              Get an indicative estimate of your solar capacity, generation, savings and PM Surya Ghar subsidy in seconds.
            </p>
          </Reveal>
          <Reveal className="mt-10">
            <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-xl sm:p-10">
              <SolarCalculator />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="mx-auto max-w-5xl px-4 py-20 sm:px-6" data-testid="faq-section">
        <Reveal className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Good to Know</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-navy">Frequently Asked Questions</h2>
        </Reveal>
        <div className="mt-10 space-y-3">
          {FAQS.slice(0, 5).map((f, i) => (
            <Reveal key={f.q + i} delay={i * 0.05}>
              <details className="group rounded-xl border border-slate-100 bg-white px-6 py-4 open:border-brand-orange/30 open:shadow-md">
                <summary className="flex cursor-pointer list-none items-center justify-between font-heading text-sm sm:text-base font-bold text-brand-navy" data-testid={`faq-home-${i}`}>
                  {f.q}
                  <ArrowRight className="h-4 w-4 shrink-0 text-brand-orange transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
              </details>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8 text-center">
          <Link to="/faq" className="inline-flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wider text-brand-blue hover:text-brand-orange transition-colors" data-testid="faq-view-all">
            View all FAQs <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-brand-blue py-16 sun-rays" data-testid="cta-section">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 px-4 text-center sm:px-6">
          <ShieldCheck className="h-10 w-10 text-brand-orange" />
          <h2 className="max-w-2xl font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            Ready to Switch to Solar?
          </h2>
          <p className="max-w-xl text-white/75">
            Get a free site assessment and a detailed quotation for your home, business, industry or farm.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={() => openQuote()} className="rounded-full bg-brand-orange px-8 py-4 font-heading text-sm font-bold uppercase tracking-wider text-white transition-all hover:bg-brand-orangedark" data-testid="cta-quote-btn">
              Get Free Quote
            </button>
            <a href={SITE.phone1} className="flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 font-heading text-sm font-bold uppercase tracking-wider text-white transition-colors hover:border-brand-orange hover:text-brand-orange" data-testid="cta-call-btn">
              <Phone className="h-4 w-4" /> {SITE.phone1Display}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
